import { Schedule, ScheduleBlock, ScheduleType } from "../../../data/entities";
import { showErrorMessage, showSuccessMessage } from "../../../utils";
import { ScheduleService } from "../../../services";
import { useSpinner } from "../../../hooks";

export function useCRUDHandlers(
  refreshData: () => void,
  showAddScheduleState?: { setter: (state: boolean) => void },
  showCloneScheduleState?: { setter: (state: boolean) => void },
  setTargetAccordionKey?: (key: string | undefined) => void, 
) {
  const service = new ScheduleService();
  const { show, hide } = useSpinner();

  const insert = async (schedule: Schedule) => {
    try {
      show();
      const ok = await service.insert(schedule);
      if (ok) {
        showSuccessMessage(`Schedule added successfully.`);
        showAddScheduleState?.setter(false);
        refreshData();
      }
    } catch (error) {
      showErrorMessage(`Error occurred while trying to add schedule.`);
    } finally {
      hide();
    }
  };

  const deleteItem = async (id: number) => {
    try {
      show();
      await service.delete(id);
      showSuccessMessage(`Schedule deleted successfully.`);
      refreshData();
    } catch (error) {
      showErrorMessage(`Error occurred while trying to delete schedule.`);
    } finally {
      hide();
    }
  };

  const clone = async (id: number, effectiveDate: Date) => {
    try {
      show();
      const clonedScheduleId = await service.cloneAsync(id, effectiveDate);
      showSuccessMessage(`Schedule cloned successfully.`);
      showCloneScheduleState?.setter(false);
      await refreshData();

      if (setTargetAccordionKey && clonedScheduleId) {
        const accordionKey = `default-${clonedScheduleId}`;
        setTargetAccordionKey(accordionKey); 
      }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          "Unable to add schedule.";
        showErrorMessage(errorMessage);
    } finally {
      hide();
    }
  }

  const deleteSchedule = async (scheduleId: number) => {
    try{
      show();
      await service.deleteScheduleAsync(scheduleId);
    
      showSuccessMessage(`Schedule deleted successfully.`);
      showAddScheduleState?.setter(false);
      refreshData();
        
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          "Unable to delete event.";
      hide();
      showErrorMessage(errorMessage);
    } finally {
      hide();
    }
      
  }

  const createSchedule = async (schedule: Schedule) => {
    try {
      show();
      const newSchedule = await service.createScheduleAsync(schedule);
      if (newSchedule) {
        showSuccessMessage(`Schedule added successfully.`);
        showAddScheduleState?.setter(false);
        await refreshData();
        
        if (setTargetAccordionKey && newSchedule.id) {
          const accordionKey = `${schedule.scheduleType === ScheduleType.Default ? 'default' : 'exception'}-${newSchedule.id}`;
          setTargetAccordionKey(accordionKey); 
        }
        
        return true;
      }
      return false;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          "Unable to add schedule.";
      showErrorMessage(errorMessage);
      return false;
    } finally {
      hide();
    }
  }

  const updateSchedule = async (scheduleBlocks: ScheduleBlock[], scheduleId: number, scheduleType: ScheduleType) => {
    try {
      show();
      await service.setScheduleBlocksAsync(scheduleId, scheduleBlocks);
      await refreshData();
      showSuccessMessage("Schedule updated successfully!");
      
      if (setTargetAccordionKey) {
        const accordionKey = `${scheduleType === ScheduleType.Default ? 'default' : 'exception'}-${scheduleId}`;
        setTargetAccordionKey(accordionKey);
      }
      
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          "Unable to update schedule.";
      showErrorMessage(errorMessage);
      return false;
    } finally {
      hide();
    }
  };

  return {
    insert,
    deleteItem,
    clone,
    createSchedule,
    deleteSchedule,
    updateSchedule
  };
}
